import { injectable, inject } from 'inversify';
import JsonRpcDispatcher, { interceptors } from '@dealroadshow/json-rpc-dispatcher';
import { SockJsAdapter } from '@dealroadshow/socket-frontend-sdk';
import { dataroomUrl as DataroomUrl } from '@dataroom/url';
import addSessionData from '@users/session/interceptors/rpc/addSessionData';
import checkSession from '@users/session/interceptors/rpc/checkSession';
import { DataroomTenant } from '@dataroom/tenant/DataroomTenant';
import { DataroomTenantContextSymbol, type IDataroomTenantContext } from '@dataroom/tenant/types';
import { validateDataroomTenant } from '@dataroom/tenant/validateDataroomTenant';
import Container from '@framework/di/Container';
import parseWrappedError from '@framework/api/Rpc/parseWrappedError';
import { ISocketDispatcherFactory } from './DI/interfaces/ISocketDispatcherFactory';
import { DISocketTypes } from './DI/types';
import { type ISocketConnection } from './DI/interfaces';

@injectable()
class SocketDispatcherFactory implements ISocketDispatcherFactory {
  constructor(
    @inject(Container) private readonly container: Container,
    @inject(DISocketTypes.SocketConnection) private readonly connection: ISocketConnection,
  ) {}

  create = (tenant: DataroomTenant | null = null) => {
    const tenantCode = tenant || this.container.get<IDataroomTenantContext>(DataroomTenantContextSymbol).tenant;

    validateDataroomTenant(tenantCode);

    const socketUrl = DataroomUrl(tenantCode).getDirectRpcUrl();

    const socketAdapter = new SockJsAdapter(socketUrl, this.connection);

    const dispatcher = new JsonRpcDispatcher(socketAdapter);

    return dispatcher
      .interceptRequest(addSessionData(this.container))
      .interceptResponse(checkSession(this.container))
      .interceptResponse(parseWrappedError())
      .interceptResponse(interceptors.response.transformErrorToException);
  };
}

export default SocketDispatcherFactory;
